import React, { useState } from "react";
import PropTypes from "prop-types";
import { styled } from "@mui/material/styles";
import MotionInfo from "../Icons/MotionInfo";
import Tooltip, { tooltipClasses } from "@mui/material/Tooltip";
import { Box, ClickAwayListener, IconButton, Typography } from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";

const BaseTooltip = ({ className, ...props }) => (
  <Tooltip {...props} classes={{ popper: className }} />
);

BaseTooltip.propTypes = {
  className: PropTypes.string,
};

const LightTooltip = styled(BaseTooltip)(({ theme }) => ({
  [`& .${tooltipClasses.tooltip}`]: {
    backgroundColor: theme.palette.common.white,
    color: theme.palette.text.primary,
    boxShadow: theme.shadows[1],
    border: "1px solid #808080",
    borderRadius: "8px",
    padding: "16px",
    maxWidth: "320px",
  },
}));

const IconWrapper = styled("span", {
  shouldForwardProp: (prop) => prop !== "isClickable",
})(({ isClickable }) => ({
  display: "inline-flex",
  alignItems: "center",
  justifyContent: "center",
  cursor: isClickable ? "pointer" : "default",
  lineHeight: 1,

  "& svg": {
    display: "block",
  },
}));

const OutlinedInfoIcon = ({ iconColor }) => (
  <span
    aria-hidden="true"
    style={{
      display: "inline-flex",
      alignItems: "center",
      justifyContent: "center",
      width: "14px",
      height: "14px",
      minWidth: "14px",
      minHeight: "14px",
      border: `1.5px solid ${iconColor}`,
      borderRadius: "50%",
      backgroundColor: "#ffffff",
      color: iconColor,
      fontFamily: "Arial, sans-serif",
      fontSize: "10px",
      fontWeight: 700,
      lineHeight: "14px",
      boxSizing: "border-box",
    }}
  >
    i
  </span>
);

OutlinedInfoIcon.propTypes = {
  iconColor: PropTypes.string.isRequired,
};

const SharedToolTip = ({
  title,
  body,
  placement = "top",
  iconColor = "#485b65",
  fontSize = "10",
  showCloseIcon = false,
  useOutlinedInfoIcon = false,
  ariaLabel,
}) => {
  const [open, setOpen] = useState(false);

  const handleOpen = () => {
    if (showCloseIcon) {
      setOpen(true);
    }
  };

  const closeTooltipIfEnabled = () => {
    if (showCloseIcon) {
      setOpen(false);
    }
  };

  const handleClose = (event) => {
    event?.preventDefault();
    event?.stopPropagation();

    setOpen(false);
  };

  const handleKeyDown = (event) => {
    if (!showCloseIcon) {
      return;
    }

    if (event.key === "Enter" || event.key === " ") {
      event.preventDefault();
      setOpen(true);
    }

    if (event.key === "Escape") {
      setOpen(false);
    }
  };

  const tooltipAriaLabel =
    ariaLabel ||
    (typeof title === "string" ? `${title} information` : "Information");

  const tooltipContent = (
    <Box
      sx={{
        position: "relative",
        paddingRight: showCloseIcon ? "28px" : 0,
      }}
    >
      {showCloseIcon && (
        <IconButton
          size="small"
          aria-label="Close tooltip"
          onClick={handleClose}
          sx={{
            position: "absolute",
            top: "-4px",
            right: "-4px",
            width: "20px",
            height: "20px",
            padding: 0,
            color: "#485b65",
            zIndex: 1,
          }}
        >
          <CloseIcon sx={{ fontSize: "16px" }} />
        </IconButton>
      )}

      <Typography variant="subtitle2" sx={{ mb: 1 }}>
        {title}
      </Typography>
      <Typography variant="body2" component="div">
        {body}
      </Typography>
    </Box>
  );

  const tooltip = (
    <LightTooltip
      enterTouchDelay={0}
      leaveTouchDelay={3000}
      placement={placement}
      open={showCloseIcon ? open : undefined}
      onOpen={showCloseIcon ? handleOpen : undefined}
      onClose={showCloseIcon ? closeTooltipIfEnabled : undefined}
      disableInteractive={false}
      slotProps={{
        popper: {
          modifiers: [
            {
              name: "offset",
              options: {
                offset: [0, 2],
              },
            },
          ],
        },
      }}
      title={tooltipContent}
    >
      <IconWrapper
        role={showCloseIcon ? "button" : undefined}
        tabIndex={showCloseIcon ? 0 : undefined}
        aria-label={tooltipAriaLabel}
        isClickable={showCloseIcon}
        onClick={showCloseIcon ? handleOpen : undefined}
        onKeyDown={showCloseIcon ? handleKeyDown : undefined}
      >
        {useOutlinedInfoIcon ? (
          <OutlinedInfoIcon iconColor={iconColor} />
        ) : (
          <MotionInfo fontSize={fontSize} />
        )}
      </IconWrapper>
    </LightTooltip>
  );

  if (!showCloseIcon) {
    return tooltip;
  }

  return (
    <ClickAwayListener onClickAway={closeTooltipIfEnabled}>
      <span style={{ display: "inline-flex" }}>{tooltip}</span>
    </ClickAwayListener>
  );
};

SharedToolTip.propTypes = {
  ariaLabel: PropTypes.string,
  body: PropTypes.node,
  fontSize: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
  iconColor: PropTypes.string,
  placement: PropTypes.string,
  showCloseIcon: PropTypes.bool,
  title: PropTypes.node.isRequired,
  useOutlinedInfoIcon: PropTypes.bool,
};

export default SharedToolTip;
